import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Replace, Plus, X } from "lucide-react";
import { WholePostComposer } from "./WholePostComposer";

const newPair = (key = `p${Date.now()}`) => ({ _k: key, old_value: "", new_value: "", category: "brand", old_url: "", new_url: "" });
const emptyMass = () => ({ name: "", account_id: "", channels: [], pairs: [newPair("p0")] });
const channelValue = (channel) => channel.username
  ? `@${String(channel.username).replace(/^@/, "")}`
  : (channel.resolved_id || channel.identifier);

export const MassReplaceDialog = ({ open, onOpenChange, accounts, destChannels, templates = [], editingJob = null, onCreated }) => {
  const [massForm, setMassForm] = useState(emptyMass);
  const [replacementType, setReplacementType] = useState("strings");
  useEffect(() => { if (open) setReplacementType("strings"); }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!editingJob) {
      setMassForm(emptyMass());
      return;
    }
    const configuredChannels = editingJob.destinations?.length
      ? editingJob.destinations
      : (editingJob.source ? [editingJob.source] : []);
    const normalizedChannels = configuredChannels.map((configured) => {
      const match = destChannels.find((channel) => [
        channel.identifier, channel.resolved_id, channelValue(channel),
      ].filter(Boolean).map(String).includes(String(configured)));
      return match ? channelValue(match) : configured;
    });
    setMassForm({
      name: editingJob.name || "",
      account_id: editingJob.source_account_id || editingJob.account_id || "",
      channels: normalizedChannels,
      pairs: editingJob.replacements?.length
        ? editingJob.replacements.map((rule, index) => ({
            _k: `edit_${index}`, old_value: rule.old_value || "", new_value: rule.new_value || "",
            category: rule.category || "brand", old_url: rule.old_url || "", new_url: rule.new_url || "",
          }))
        : [newPair("p0")],
    });
  }, [open, editingJob, destChannels]);

  const setPair = (i, field, val) =>
    setMassForm((f) => ({ ...f, pairs: f.pairs.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)) }));
  const addPair = () => setMassForm((f) => ({ ...f, pairs: [...f.pairs, newPair()] }));
  const removePair = (i) => setMassForm((f) => ({ ...f, pairs: f.pairs.filter((_, idx) => idx !== i) }));

  const applyTemplate = (templateId) => {
    const t = templates.find((x) => x.id === templateId);
    if (!t || !t.rules?.length) return;
    setMassForm((f) => {
      const existing = f.pairs.filter((p) => p.old_value.trim() || p.new_value.trim());
      const added = t.rules.map((r, i) => ({ _k: `t${Date.now()}_${i}`, old_value: r.old_value, new_value: r.new_value || "", category: r.category || "brand", old_url: r.old_url || "", new_url: r.new_url || "" }));
      const merged = [...existing, ...added];
      return { ...f, pairs: merged.length ? merged : [newPair()] };
    });
    toast.success(`Applied template "${t.name}"`);
  };

  const toggleChannel = (channel) => setMassForm((current) => ({
    ...current,
    channels: current.channels.includes(channel)
      ? current.channels.filter((value) => value !== channel)
      : [...current.channels, channel],
  }));

  const saveMassReplace = async () => {
    const pairs = massForm.pairs.filter((p) => p.old_value.trim());
    if (!massForm.name || !massForm.account_id || !massForm.channels.length)
      return toast.error("Name, account and at least one target channel are required");
    if (pairs.length === 0) return toast.error("Add at least one replacement (old → new)");
    if (pairs.some((p) => p.category === "hyperlink" && (!p.old_value.trim() || !p.old_url?.trim() || !p.new_value.trim() || !p.new_url?.trim())))
      return toast.error("Hyperlink rules require old and new linked Text + Target values");
    try {
      const body = {
        name: massForm.name,
        account_id: massForm.account_id,
        channels: massForm.channels,
        replacements: pairs.map((p) => ({ old_value: p.old_value, new_value: p.new_value, category: p.category || "brand", old_url: p.old_url || "", new_url: p.new_url || "" })),
      };
      if (editingJob) await api.put(`/jobs/${editingJob.id}/mass-replace`, body);
      else await api.post("/jobs/mass-replace", body);
      toast.success(editingJob ? "Mass replace job updated" : "Mass replace job created");
      onOpenChange(false);
      setMassForm(emptyMass());
      onCreated();
    } catch (e) {
      toast.error(errMsg(e, editingJob ? "Failed to update job" : "Failed to create job"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!editingJob && <DialogTrigger asChild>
        <Button variant="outline" className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] font-semibold" data-testid="mass-replace-btn">
          <Replace className="h-4 w-4 mr-2" /> Mass Replace
        </Button>
      </DialogTrigger>}
      <DialogContent className={`bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] w-[95vw] ${replacementType === "whole" ? "max-w-6xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto overflow-x-hidden`}>
        <DialogHeader>
          <DialogTitle className="font-head">{editingJob ? "Edit Mass Replace Job" : "Mass Replace"}</DialogTitle>
          <DialogDescription className="text-[var(--tg-faint)]">
            {replacementType === "strings" ? "Scan every message in a destination channel and edit-in-place any that contain your strings. Matching is case-insensitive; the new value is applied exactly as typed." : "Browse a date and replace one exact historical post in place."}
          </DialogDescription>
        </DialogHeader>
        {!editingJob && <div className="space-y-2"><Label className="label-caps">Replacement type</Label><div className="grid grid-cols-2 gap-2">{[["strings", "Text / Value Replacement", "Find old strings and replace them in historical posts."], ["whole", "Whole Post Replacement", "Select a historical post and replace its complete content."]].map(([value, title, description]) => <button type="button" key={value} aria-pressed={replacementType === value} onClick={() => setReplacementType(value)} className={`p-3 rounded-lg border text-left ${replacementType === value ? "border-[var(--c-sky)] bg-[var(--tg-hover)]" : "border-[var(--tg-border)]"}`}><strong className="block text-sm">{title}</strong><span className="text-xs text-[var(--tg-muted)]">{description}</span></button>)}</div></div>}
        {replacementType === "whole" ? <WholePostComposer accounts={accounts} /> : <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="label-caps">Job Name</Label>
            <Input data-testid="mass-name" value={massForm.name} onChange={(e) => setMassForm({ ...massForm, name: e.target.value })} placeholder="Rebrand old links" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)]" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Account (must be admin of the channel)</Label>
            <Select value={massForm.account_id} onValueChange={(v) => setMassForm({ ...massForm, account_id: v })}>
              <SelectTrigger data-testid="mass-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                {accounts.filter((a) => a.authorized).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="label-caps">Target Channels — select one or more</Label>
              <span className="text-[10px] font-semibold text-[var(--c-sky)]">{massForm.channels.length} selected</span>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border border-[var(--tg-border)] bg-[var(--tg-bg)] p-2 space-y-1" data-testid="mass-channels">
              {destChannels.length === 0 && <div className="px-2 py-3 text-xs text-[var(--tg-faint)]">Add a destination channel first</div>}
              {destChannels.map((c) => {
                const value = channelValue(c);
                const selected = massForm.channels.includes(value);
                return (
                  <button key={c.id} type="button" onClick={() => toggleChannel(value)}
                    className={`w-full flex items-center gap-2 rounded px-2.5 py-2 text-left text-sm transition-colors ${selected ? "bg-[rgb(var(--c-sky-rgb)/0.14)] text-[var(--tg-strong)]" : "hover:bg-[var(--tg-hover)] text-[var(--tg-muted)]"}`}
                    data-testid={`mass-channel-${c.id}`}>
                    <span className={`h-4 w-4 rounded border flex items-center justify-center text-[10px] ${selected ? "border-[var(--c-sky)] bg-[var(--c-sky)] text-white" : "border-[var(--tg-border)]"}`}>{selected ? "✓" : ""}</span>
                    <span className="truncate">{c.title || value}</span>
                    <span className="ml-auto text-[10px] font-mono text-[var(--tg-faint)] truncate max-w-[45%]">{value}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Replacements (old → new)</Label>
            {templates.length > 0 && (
              <Select value="" onValueChange={applyTemplate}>
                <SelectTrigger data-testid="mass-template-picker" className="bg-[var(--tg-bg)] border-[var(--tg-border)]">
                  <SelectValue placeholder="Apply a template (prefills rows)" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`mass-template-${t.id}`}>
                      {t.name} ({t.rules?.length || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="space-y-2">
              {massForm.pairs.map((p, i) => (
                <div key={p._k} className={`rounded-lg border p-3 space-y-3 ${p.category === "hyperlink" ? "border-[rgb(var(--c-violet-rgb)/0.40)] bg-[rgb(var(--c-violet-rgb)/0.05)]" : p.category === "ocr_skip" ? "border-[rgb(var(--c-amber-rgb)/0.40)] bg-[rgb(var(--c-amber-rgb)/0.05)]" : "border-[var(--tg-border)]"}`} data-testid={`mass-pair-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                  <Select value={p.category || "brand"} onValueChange={(value) => setPair(i, "category", value)}>
                    <SelectTrigger className="w-[150px] bg-[var(--tg-bg)] border-[var(--tg-border)] text-xs" data-testid={`mass-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]"><SelectItem value="username">Username</SelectItem><SelectItem value="url">URL</SelectItem><SelectItem value="brand">Brand</SelectItem><SelectItem value="hyperlink">Hyperlink</SelectItem><SelectItem value="ocr_skip">OCR Image Skip</SelectItem></SelectContent>
                  </Select>
                  {massForm.pairs.length > 1 && (
                    <button onClick={() => removePair(i)} className="text-[var(--tg-faint)] hover:text-[var(--c-rose)]" data-testid={`mass-remove-${i}`}><X className="h-4 w-4" /></button>
                  )}
                  </div>
                  {p.category === "hyperlink" ? <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center"><div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-rose)]">Old hyperlink</div><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="Any old linked text" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`mass-old-${i}`} /><Input value={p.old_url || ""} onChange={(e) => setPair(i, "old_url", e.target.value)} placeholder="Old target: URL or @username" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`mass-old-url-${i}`} /></div><span className="text-[var(--tg-faint)]">→</span><div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-emerald)]">New hyperlink</div><Input value={p.new_value} onChange={(e) => setPair(i, "new_value", e.target.value)} placeholder="Any new linked text" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`mass-new-${i}`} /><Input value={p.new_url || ""} onChange={(e) => setPair(i, "new_url", e.target.value)} placeholder="New target: URL or @username" className="bg-[var(--tg-bg)] border-[var(--tg-border)] font-mono text-xs" data-testid={`mass-new-url-${i}`} /></div></div> : p.category === "ocr_skip" ? <div className="space-y-2"><div className="text-[10px] uppercase tracking-wider text-[var(--c-amber)] font-semibold">Skip image when OCR finds</div><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="Text to detect in the image" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-amber)] font-mono text-xs" data-testid={`mass-old-${i}`} /><p className="text-[10px] text-[var(--tg-faint)]">No replacement value is required.</p></div> : <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center"><Input value={p.old_value} onChange={(e) => setPair(i, "old_value", e.target.value)} placeholder="old value" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] font-mono text-xs" data-testid={`mass-old-${i}`} /><span className="text-[var(--tg-faint)]">→</span><Input value={p.new_value} onChange={(e) => setPair(i, "new_value", e.target.value)} placeholder="new value" className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] font-mono text-xs" data-testid={`mass-new-${i}`} /></div>}
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={addPair} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="mass-add-pair"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add rule</Button>
          </div>
          <Button onClick={saveMassReplace} className="w-full bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="create-mass-btn">
            {editingJob ? "Save Changes" : "Create Mass Replace Job"}
          </Button>
        </div>}
      </DialogContent>
    </Dialog>
  );
};
